'use strict';

var _ = require('lodash');
var LogitSharesDataTransformationService = function LogitSharesDataTransformationService($filter, calibrationAnalysisConstants, momentService, logitSharesNormalizationService) {
  var readingTimeRegex = /(-?\d+)d \d+h \d+m/;
  var rmNumberFormat = $filter('rmNumberFormat');
  function transformLogitDataForGrid(logitProfiles, maxRtp) {
    return _.chain(logitProfiles).map(function (profile) {
      var logitParams = profile.properties.logitParams;
      _.forEach(logitParams, function (param) {
        setDefaults(param);
      });
      return profile;
    }).reduce(function (gridData, logitProfile) {
      var logitParams = extractLogitParams(maxRtp, logitProfile);
      var attributes = logitProfile.attributes;
      attributes.market = attributes.origin + '-' + attributes.destination;
      var transformedParams = transformLogitParams(logitParams, attributes);
      return gridData.concat(transformedParams);
    }, []).value();
  }
  function transformLogitParams(logitParams, attributes) {
    return _.reduce(logitParams, function (transformedParam, logitParam) {
      attributes.rtpBucket = logitParam.rtpBucket;
      attributes.readingTime = logitParam.readingTime;
      attributes.key = logitParam.key;
      var flattenedTravelTimeData = flattenTravelTimeLogitShares(logitParam, attributes);
      var flattenedFareData = flattenFareLogitShares(logitParam, attributes);
      var shareComputedData = logitSharesNormalizationService.transformTodLogitsToShares(logitParam.logitTimeOfDayPreferences);
      var flattenedTodData = flattenTimeOfDayLogitShares(shareComputedData, attributes);
      return transformedParam.concat(flattenedTravelTimeData, flattenedFareData, flattenedTodData);
    }, []);
  }
  function flattenLogitShares(logitValues, attributes, logitType) {
    switch (logitType) {
      case calibrationAnalysisConstants.CHART_TYPES.TIME_OF_DAY:
        return flattenTimeOfDayLogitShares(logitValues, attributes);
      case calibrationAnalysisConstants.CHART_TYPES.TRAVEL_TIME:
        return flattenTravelTimeLogitShares(logitValues, attributes);
      case calibrationAnalysisConstants.CHART_TYPES.FARE:
        return flattenFareLogitShares(logitValues, attributes);
    }
  }
  function flattenTimeOfDayLogitShares(todLogits, attributes) {
    var moment = momentService.now(momentService.FIELD.DEPARTURE_TIME).setHour(23).setMinute(59);
    setDayOfWeekAttributes(attributes);
    return _.chain(todLogits).reduceRight(function (transformedData, todLogit) {
      var tod = todLogit.timeOfDayStart;
      var todHour = parseInt(tod) / 100;
      var fromTime = tod.substring(0, 2) + ':' + tod.substring(2, 4);
      var toTime = moment.format(calibrationAnalysisConstants.READING_TIME_GRID_DISPLAY_FORMAT);
      var range = fromTime + ' - ' + toTime;
      moment = moment.setHour(todHour - 1);
      var todLogitData = {
        sortIndex: 1,
        attribute: 'Time of Day',
        attributeValue: range,
        modelShare: rmNumberFormat(todLogit.modelShare, 'NUMBER'),
        userShare: _.isNull(todLogit.userShare) ? null : rmNumberFormat(todLogit.userShare, 'NUMBER'),
        effectiveShare: rmNumberFormat(todLogit.effectiveShare, 'NUMBER'),
        timeOfDay: todLogit.timeOfDayStart,
        type: calibrationAnalysisConstants.CHART_TYPES.TIME_OF_DAY
      };
      if (!_.isUndefined(todLogit._old)) {
        todLogitData._old = todLogit._old;
      }
      _.extend(todLogitData, attributes);
      return transformedData.concat(todLogitData);
    }, []).reverse().value();
  }
  function flattenTravelTimeLogitShares(reviewData, attributes) {
    setDayOfWeekAttributes(attributes);
    var travelTimeLogitData = {
      sortIndex: 2,
      attribute: 'Travel Time',
      attributeValue: '-',
      modelShare: rmNumberFormat(reviewData.modelElapsedTimeLogit, 'NUMBER_LOGIT'),
      userShare: _.isNull(reviewData.userElapsedTimeLogit) ? null : rmNumberFormat(reviewData.userElapsedTimeLogit, 'NUMBER_LOGIT'),
      effectiveShare: _.isNull(reviewData.userElapsedTimeLogit) ? rmNumberFormat(reviewData.modelElapsedTimeLogit, 'NUMBER_LOGIT') : rmNumberFormat(reviewData.userElapsedTimeLogit, 'NUMBER_LOGIT'),
      type: calibrationAnalysisConstants.CHART_TYPES.TRAVEL_TIME
    };
    return _.extend(travelTimeLogitData, attributes);
  }
  function flattenFareLogitShares(reviewData, attributes) {
    setDayOfWeekAttributes(attributes);
    var fareLogitData = {
      sortIndex: 3,
      attribute: 'Fare',
      attributeValue: '-',
      modelShare: rmNumberFormat(reviewData.modelRelativeFareLogit, 'NUMBER_LOGIT'),
      userShare: _.isNull(reviewData.userRelativeFareLogit) ? null : rmNumberFormat(reviewData.userRelativeFareLogit, 'NUMBER_LOGIT'),
      effectiveShare: _.isNull(reviewData.userRelativeFareLogit) ? rmNumberFormat(reviewData.modelRelativeFareLogit, 'NUMBER_LOGIT') : rmNumberFormat(reviewData.userRelativeFareLogit, 'NUMBER_LOGIT'),
      type: calibrationAnalysisConstants.CHART_TYPES.FARE
    };
    return _.extend(fareLogitData, attributes);
  }
  function setDayOfWeekAttributes(attributes) {
    var dowNotation = _.startCase(_.lowerCase(attributes.dayOfWeek).substring(0, 3));
    return _.extend(attributes, {
      dayOfWeekShort: dowNotation,
      dayOfWeekInt: _.findIndex(calibrationAnalysisConstants.DOW, {
        label: dowNotation
      })
    });
  }
  function prepareChartData(logitShareValues, chartType, viewType) {
    var normalizedShares = _.filter(logitShareValues, function (logitShares) {
      return !_.isNil(logitShares.userShare);
    });
    var isNormalized = normalizedShares.length > 0;
    var showFixedData = isFixedDataShown(viewType);
    var showScheduledData = isScheduleDataShown(viewType);
    return _.reduce(logitShareValues, function (chartData, logit) {
      var fixedShare = showFixedData ? logit.modelShare : 0;
      var scheduledShare = showScheduledData ? logit.scheduledShare : 0;
      var userShare = isNormalized ? logit.effectiveShare : 0;
      var publishedShare = _.isNull(logit.publishedShare) ? null : logit.publishedShare;
      var publishedScheduledShare = _.isNull(logit.publishedScheduledShare) ? null : logit.publishedScheduledShare;
      var publishedUserShare = _.isNull(logit.publishedUserShare) ? null : logit.publishedUserShare;
      if (!_.isNull(logit.userShare) && !_.isUndefined(logit.userShare)) {
        logit.userShare = _.toNumber(logit.userShare.toFixed(2));
      }
      var effectiveShare = logit.effectiveShare;
      var tickValue;
      if (chartType === calibrationAnalysisConstants.CHART_TYPES.TIME_OF_DAY) {
        tickValue = _.toNumber(logit.timeOfDayStart) / 100;
      } else {
        tickValue = logit.key;
      }
      var userData = {
        d1: calibrationAnalysisConstants.BAR_DATA_TYPES.USER + '-' + tickValue,
        m1: userShare,
        m2: 0,
        user: logit.userShare,
        model: fixedShare,
        eff: effectiveShare,
        modelfill: calibrationAnalysisConstants.COLOR_THEME.FIXED,
        m1fill: calibrationAnalysisConstants.COLOR_THEME.USER,
        m2fill: calibrationAnalysisConstants.COLOR_THEME.SCHEDULED,
        type: 'user',
        tiles: [],
        current: _.isNull(publishedUserShare) ? [] : [{
          value: publishedUserShare,
          css: 'user-circle'
        }],
        curr: publishedUserShare,
        scheduled: logit.scheduledShare,
        scheduledCurr: publishedScheduledShare
      };
      var modelData = {
        d1: calibrationAnalysisConstants.BAR_DATA_TYPES.SYSTEM + '-' + tickValue,
        m1: fixedShare,
        m2: scheduledShare,
        user: logit.userShare,
        eff: effectiveShare,
        type: 'model',
        efffill: calibrationAnalysisConstants.COLOR_THEME.USER,
        m1fill: calibrationAnalysisConstants.COLOR_THEME.FIXED,
        m2fill: calibrationAnalysisConstants.COLOR_THEME.SCHEDULED,
        tiles: scheduledShare > 0 && scheduledShare <= fixedShare ? [{
          m2: scheduledShare,
          fill: calibrationAnalysisConstants.COLOR_THEME.SCHEDULED
        }] : [],
        current: getCurrentDataAsCircles(publishedShare, publishedScheduledShare, viewType),
        curr: publishedShare,
        scheduled: logit.scheduledShare,
        scheduledCurr: publishedScheduledShare
      };
      return chartData.concat([modelData, userData]);
    }, []);
  }
  function getCurrentDataAsCircles(publishedShare, publishedScheduledShare, viewType) {
    var circles = [];
    if (isFixedDataShown(viewType) && !_.isNull(publishedShare)) {
      circles.push({
        value: publishedShare,
        css: 'model-circle'
      });
    }
    if (isScheduleDataShown(viewType) && !_.isNull(publishedScheduledShare)) {
      circles.push({
        value: publishedScheduledShare,
        css: 'scheduled-circle'
      });
    }
    return circles;
  }
  function isFixedDataShown(viewType) {
    return viewType === calibrationAnalysisConstants.REVIEW_VIEW_TYPES.FIXED || viewType === calibrationAnalysisConstants.REVIEW_VIEW_TYPES.FIXED_SCHEDULED;
  }
  function isScheduleDataShown(viewType) {
    return viewType === calibrationAnalysisConstants.REVIEW_VIEW_TYPES.SCHEDULED || viewType === calibrationAnalysisConstants.REVIEW_VIEW_TYPES.FIXED_SCHEDULED;
  }
  function transformServiceData(services, logitSharesRequest) {
    return _.reduce(services, function (transformedService, service) {
      var extractedData = _.pick(service, ['hoursToDeparture', 'departureDate', 'elapsedTime']);
      extractedData.fare = _.chain(service.serviceClassPointOfSales).filter(function (serviceClassPos) {
        return serviceClassPos.significantPOS === logitSharesRequest.significantPos && serviceClassPos.classGroup === logitSharesRequest.classGroup;
      }).map('fare').mean().value();
      return transformedService.concat(extractedData);
    }, []);
  }
  function extractLogitParams(maxRtp, logitHepProfile, publishedLogitProfile) {
    var logitParams = logitHepProfile.properties.logitParams;
    var publishedLogitParams = _.isUndefined(publishedLogitProfile) ? [] : publishedLogitProfile.properties.logitParams;
    var lastReadingTime = maxRtp;
    return _.chain(logitParams).reduceRight(function (chartData, logitParam) {
      var parsedReadingTime = _.toNumber(logitParam.startReadingTimeInterval.match(readingTimeRegex)[1]);
      var readingTime = parsedReadingTime < 0 ? 0 : parsedReadingTime;
      var rtpBucket = _.join([readingTime, lastReadingTime], ' - ');
      lastReadingTime = readingTime - 1;
      var params = _.pick(logitParam, calibrationAnalysisConstants.LOGIT_PARAM_KEYS_TO_COPY);
      if (!_.isEmpty(publishedLogitParams)) {
        var publishedParams = getPublishedData(logitParam, publishedLogitParams);
        mergePublishedData(params, publishedParams);
      }
      var logitData = {
        rowIndex: readingTime,
        readingTime: parsedReadingTime,
        rtpBucket: rtpBucket,
        key: logitHepProfile.hepKey
      };
      _.extend(logitData, params);
      return chartData.concat(logitData);
    }, []).reverse().value();
  }
  function setDefaults(logitParams) {
    if (_.isEmpty(logitParams)) {
      return;
    }
    logitParams.modelElapsedTimeLogit = _.isUndefined(logitParams.modelElapsedTimeLogit) ? logitParams.elapsedTimeLogit : logitParams.modelElapsedTimeLogit;
    logitParams.userElapsedTimeLogit = _.isUndefined(logitParams.userElapsedTimeLogit) ? null : logitParams.userElapsedTimeLogit;
    logitParams.orginalElapsedTimeLogit = logitParams.modelElapsedTimeLogit;
    logitParams.originalUserElapsedTimeLogit = _.isUndefined(logitParams.originalUserElapsedTimeLogit) ? logitParams.userElapsedTimeLogit : logitParams.originalUserElapsedTimeLogit;
    logitParams.modelRelativeFareLogit = _.isUndefined(logitParams.modelRelativeFareLogit) ? logitParams.relativeFareLogit : logitParams.modelRelativeFareLogit;
    logitParams.userRelativeFareLogit = _.isUndefined(logitParams.userRelativeFareLogit) ? null : logitParams.userRelativeFareLogit;
    logitParams.originalFareLogit = logitParams.modelRelativeFareLogit;
    logitParams.originalUserFareLogit = _.isUndefined(logitParams.originalUserFareLogit) ? logitParams.userRelativeFareLogit : logitParams.originalUserFareLogit;
    _.map(logitParams.logitTimeOfDayPreferences, function (todPreference) {
      if (_.isUndefined(todPreference.modelTimeOfDayPreferenceCoefficient)) {
        todPreference.modelTimeOfDayPreferenceCoefficient = todPreference.timeOfDayPreferenceCoefficient;
        todPreference.userTimeOfDayPreferenceCoefficient = null;
      }
    });
  }
  function getPublishedData(logitParam, publishedLogitParams) {
    var logitParamId = logitParam._id;
    var publishedReadingTimeData = _.find(publishedLogitParams, {
      _id: logitParamId
    });
    if (_.isUndefined(publishedReadingTimeData)) {
      return {};
    }
    var params = _.pick(publishedReadingTimeData, calibrationAnalysisConstants.LOGIT_PARAM_KEYS_TO_COPY);
    setDefaults(params);
    return params;
  }
  function mergePublishedData(params, publishedParams) {
    params.publishedModelElapsedTimeLogit = publishedParams.modelElapsedTimeLogit;
    params.publishedUserElapsedTimeLogit = publishedParams.userElapsedTimeLogit;
    params.publishedModelFareLogit = publishedParams.modelRelativeFareLogit;
    params.publishedUserFareLogit = publishedParams.userRelativeFareLogit;
    _.map(params.logitTimeOfDayPreferences, function (todPreferences) {
      var publishedTodData = _.find(publishedParams.logitTimeOfDayPreferences, {
        id: todPreferences.id
      });
      if (!_.isUndefined(publishedTodData)) {
        todPreferences.publishedTimeOfDayPreferenceCoefficient = publishedTodData.timeOfDayPreferenceCoefficient;
        todPreferences.publishedModelTimeOfDayPreferenceCoefficient = publishedTodData.modelTimeOfDayPreferenceCoefficient;
        todPreferences.publishedUserTimeOfDayPreferenceCoefficient = publishedTodData.userTimeOfDayPreferenceCoefficient;
      }
    });
  }
  function transformProfileForSave(logitProfile) {
    var attributes = {
      attributes: _.pick(logitProfile.attributes, calibrationAnalysisConstants.LOGIT_PROFILE_ATTRIBUTE_KEYS)
    };
    var logitParams = logitProfile.properties.logitParams;
    logitProfile.properties.logitParams = pickLogitParamValuesForSave(logitParams);
    return _.extend({}, attributes, _.pick(logitProfile, calibrationAnalysisConstants.LOGIT_HEP_PROFILE_KEYS));
  }
  function pickLogitParamValuesForSave(logitParams) {
    return _.reduce(logitParams, function (transformedLogitParams, logitParam) {
      setEffectiveFareAndElapsedTimeLogits(logitParam);
      var fareAndTravelTimeProperties = _.pick(logitParam, calibrationAnalysisConstants.LOGIT_PARAM_KEYS);
      var logitTimeOfDayPreferences = _.reduce(logitParam.logitTimeOfDayPreferences, function (transformedTodPref, todPref) {
        var requiredTodPrefAttributes = _.pick(todPref, calibrationAnalysisConstants.TIME_OF_DAY_PREFERENCES_KEYS);
        return transformedTodPref.concat(requiredTodPrefAttributes);
      }, []);
      _.extend(fareAndTravelTimeProperties, {
        logitTimeOfDayPreferences: logitTimeOfDayPreferences
      });
      return transformedLogitParams.concat(fareAndTravelTimeProperties);
    }, []);
  }
  function setEffectiveFareAndElapsedTimeLogits(logitParam) {
    logitParam.elapsedTimeLogit = _.isNil(logitParam.userElapsedTimeLogit) ? logitParam.modelElapsedTimeLogit : logitParam.userElapsedTimeLogit;
    logitParam.relativeFareLogit = _.isNil(logitParam.userRelativeFareLogit) ? logitParam.modelRelativeFareLogit : logitParam.userRelativeFareLogit;
  }
  function updateLogitProfile(overrides, logitProfiles) {
    _.chain(overrides).filter(function (override) {
      return override.type === calibrationAnalysisConstants.CHART_TYPES.TRAVEL_TIME || override.type === calibrationAnalysisConstants.CHART_TYPES.FARE;
    }).map(function (override) {
      var impactedProfile = _.find(logitProfiles, {
        hepKey: override.key
      });
      var logitParams = impactedProfile.properties.logitParams;
      _.chain(logitParams).filter(function (logitParam) {
        var readingTime = logitParam.startReadingTimeInterval.match(readingTimeRegex)[1];
        return _.parseInt(readingTime) === override.readingTime;
      }).map(function (logitParam) {
        if (override.type === calibrationAnalysisConstants.CHART_TYPES.TRAVEL_TIME) {
          logitParam.userElapsedTimeLogit = _.isNull(override.userShare) ? null : _.toNumber(override.userShare);
        } else {
          logitParam.userRelativeFareLogit = _.isNull(override.userShare) ? null : _.toNumber(override.userShare);
        }
        return logitParam;
      }).value();
      return impactedProfile;
    }).value();
  }
  return {
    transformLogitDataForGrid: transformLogitDataForGrid,
    flattenLogitShares: flattenLogitShares,
    prepareChartData: prepareChartData,
    transformServiceData: transformServiceData,
    extractLogitParams: extractLogitParams,
    setDefaults: setDefaults,
    transformProfileForSave: transformProfileForSave,
    updateLogitProfile: updateLogitProfile
  };
};
LogitSharesDataTransformationService.$inject = ['$filter', 'CalibrationAnalysisConstants', 'MomentService', 'LogitSharesNormalizationService'];
module.exports = LogitSharesDataTransformationService;